import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getTransaction, processRefund } from '../services/transactionsService';
import { getUserProfile } from '../services/profilesService';
import { listVendorsForUser } from '../services/vendorsService';
import { releaseBoothRemote } from '../services/reservationsService';
import { getEvent } from '../services/eventsService';
import { updateCartItemAction, loadCartItemsByStatus } from '../services/cartItemsService';
import { decreasePurchasedCapacity } from '../services/attendeeCapacityService';
import { sfVendorRegistration, getFunctionConfig } from '../services/defaultsService';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../sdk_initialization';
import './ProfilePage.css';

const TransactionDetail = () => {
  const navigate = useNavigate();
  const { transactionId } = useParams();
  const { currentUser } = useAuth();
  
  const [userProfile, setUserProfile] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [transaction, setTransaction] = useState(null);
  const [vendorProfile, setVendorProfile] = useState(null);
  const [vendorData, setVendorData] = useState(null); // Vendor record from vendors collection
  const [eventData, setEventData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Refund form state
  const [showRefundForm, setShowRefundForm] = useState(false);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [refundProcessing, setRefundProcessing] = useState(false);
  const [refundError, setRefundError] = useState('');
  const [refundSuccess, setRefundSuccess] = useState('');
  const [selectedBooths, setSelectedBooths] = useState([]);
  const [selectAllBooths, setSelectAllBooths] = useState(false);
  
  // Refund fee configuration (from event, but can be overridden by admin)
  const [refundFeePercentage, setRefundFeePercentage] = useState('');
  const [refundFeeAmount, setRefundFeeAmount] = useState('');
  const [applyRefundFeePerBooth, setApplyRefundFeePerBooth] = useState(false);
  const [additionalRefundAmount, setAdditionalRefundAmount] = useState('');
  const [refundAttendeeCount, setRefundAttendeeCount] = useState(''); // Number of attendees being refunded

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;
  // Metadata modal visibility
  const [showMetadataModal, setShowMetadataModal] = useState(false);

  // Derived state - check if this is an attendee purchase
  const isAttendeePurchase = transaction?.metadata?.purchaseType === 'attendees';

  // Load current user profile to check if admin
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!currentUser?.uid) return;
      try {
        const profile = await getUserProfile(currentUser.uid);
        if (!cancelled) {
          setUserProfile(profile);
          const adminUser = profile?.userType === 'Admin' || profile?.userType === 'EventAdmin' || profile?.userType === 'Organizer';
          setIsAdmin(adminUser);
        }
      } catch (e) {
  // Removed console.error
      }
    })();
    return () => { cancelled = true; };
  }, [currentUser]);

  // Load transaction details
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!transactionId) {
        setError('No transaction ID provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError('');
        
        const txn = await getTransaction(transactionId);
        
        if (!txn) {
          setError('Transaction not found');
          setLoading(false);
          return;
        }

        // Check authorization - admin or owner
        if (!cancelled) {
          const adminUser = userProfile?.userType === 'Admin' || userProfile?.userType === 'EventAdmin' || userProfile?.userType === 'Organizer';
          if (!adminUser && txn.profileId !== currentUser?.uid) {
            setError('You do not have permission to view this transaction');
            setLoading(false);
            return;
          }

          setTransaction(txn);
          
          // Load event data for refund fee defaults
          if (txn.eventId) {
            try {
              const event = await getEvent(txn.eventId);
              if (!cancelled && event) {
                setEventData(event);
                // Set default refund fee values from event
                setRefundFeePercentage(event.refundPercentage ?? '');
                setRefundFeeAmount(event.refundAmount ?? '');
                setApplyRefundFeePerBooth(event.applyRefundFeePerBooth ?? false);
              }
            } catch (e) {
              // Removed console.error
            }
          }
          
          // Load vendor profile
          if (txn.profileId) {
            try {
              const vendorProf = await getUserProfile(txn.profileId);
              if (!cancelled) setVendorProfile(vendorProf);
              
              // Also try to load vendor data from vendors collection
              // This is a fallback for when metadata.vendorName is missing
              try {
                const vendors = await listVendorsForUser(txn.profileId);
                if (!cancelled && vendors && vendors.length > 0) {
                  setVendorData(vendors[0]); // Use first vendor record
                }
              } catch (vendorErr) {
                // Removed console.error
              }
            } catch (e) {
              // Removed console.error
            }
          }
          
          // Set default refund amount to full amount
          setRefundAmount(((txn.amount || 0) / 100).toFixed(2));
          
          setLoading(false);
        }
      } catch (e) {
        if (!cancelled) {
          // Removed console.error
          setError(e?.message || 'Failed to load transaction');
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [transactionId, currentUser, userProfile]);

  // Format timestamp
  const formatTimestamp = (ts) => {
    try {
      if (!ts) return 'N/A';
      if (typeof ts.toMillis === 'function') return new Date(ts.toMillis()).toLocaleString();
      if (ts.seconds) return new Date(ts.seconds * 1000).toLocaleString();
      const d = new Date(ts);
      return isNaN(d.getTime()) ? 'N/A' : d.toLocaleString();
    } catch {
      return 'N/A';
    }
  };

  // Format currency
  const formatCurrency = (amountInCents) => {
    return `$${((amountInCents || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Get status badge color
  const getStatusColor = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'succeeded') return '#10b981';
    if (s === 'failed') return '#ef4444';
    if (s === 'incomplete') return '#f59e0b';
    if (s === 'refunded') return '#9333ea';
    if (s === 'partially_refunded') return '#d946ef';
    return '#6b7280';
  };

  // Calculate refundable amount
  const getRefundableAmount = () => {
    if (!transaction) return 0;
    const totalRefunded = transaction.totalRefunded || 0;

    // Remaining balance = Original Amount - Total Refunded.
    // totalRefunded is already the net amount actually refunded by Stripe,
    // so cancellation fees should not be subtracted again here.
    return Math.max(0, transaction.amount - totalRefunded);
  };

  // Check if a booth has been refunded
  const isBoothRefunded = (boothId) => {
    if (!transaction || !transaction.refundHistory) return false;
    return transaction.refundHistory.some(refund => 
      refund.status === 'succeeded' && 
      refund.boothIds && 
      refund.boothIds.includes(boothId)
    );
  };

  // Calculate total price of selected booths
  const calculateSelectedBoothsTotal = React.useCallback(() => {
    if (!transaction || !transaction.booths) return 0;
    return selectedBooths.reduce((total, boothId) => {
      const booth = transaction.booths.find(b => b.id === boothId);
      return total + (booth?.price || 0);
    }, 0);
  }, [transaction, selectedBooths]);

  // Calculate refund fee based on configuration
  const calculateRefundFee = React.useCallback((baseAmount) => {
    let feeAmount = 0;
    
    // For attendee refunds, use the refund count, otherwise use total from metadata
    const attendeeCount = isAttendeePurchase ? (parseInt(refundAttendeeCount) || 0) : 0;
    
    // If applying fee per booth/attendee, multiply by count
    const multiplier = applyRefundFeePerBooth 
      ? (isAttendeePurchase ? attendeeCount : selectedBooths.length) 
      : 1;
    
    // Apply percentage fee
    if (refundFeePercentage && !isNaN(parseFloat(refundFeePercentage))) {
      const percentageFee = (baseAmount * parseFloat(refundFeePercentage)) / 100;
      feeAmount += percentageFee;
    }
    
    // Apply fixed amount fee (in dollars, convert to cents)
    if (refundFeeAmount && !isNaN(parseFloat(refundFeeAmount))) {
      const fixedFee = parseFloat(refundFeeAmount) * 100 * multiplier;
      feeAmount += fixedFee;
    }
    
    return Math.round(feeAmount);
  }, [refundFeePercentage, refundFeeAmount, applyRefundFeePerBooth, selectedBooths.length, isAttendeePurchase, refundAttendeeCount]);

  // Calculate final refund amount after deducting fees
  const calculateFinalRefundAmount = React.useCallback(() => {
    let baseAmount = calculateSelectedBoothsTotal();
    
    // Add attendee refund amount if this is an attendee purchase
    if (isAttendeePurchase && refundAttendeeCount > 0) {
      const pricePerAttendee = parseFloat(transaction?.metadata?.pricePerAttendee) || 0;
      const attendeeRefundAmount = Math.round(pricePerAttendee * parseInt(refundAttendeeCount) * 100);
      baseAmount += attendeeRefundAmount;
    }
    
    const fee = calculateRefundFee(baseAmount);
    return Math.max(0, baseAmount - fee);
  }, [calculateSelectedBoothsTotal, calculateRefundFee, isAttendeePurchase, transaction?.metadata, refundAttendeeCount]);

  // Handle booth selection
  const handleBoothSelection = (boothId, isChecked) => {
    if (isChecked) {
      setSelectedBooths(prev => [...prev, boothId]);
    } else {
      setSelectedBooths(prev => prev.filter(id => id !== boothId));
      setSelectAllBooths(false);
    }
  };

  // Handle select all booths
  const handleSelectAllBooths = (isChecked) => {
    setSelectAllBooths(isChecked);
    if (isChecked && transaction?.booths) {
      // Only select booths that haven't been refunded
      const selectableBooths = transaction.booths
        .filter(booth => !isBoothRefunded(booth.id))
        .map(booth => booth.id);
      setSelectedBooths(selectableBooths);
    } else {
      setSelectedBooths([]);
    }
  };

  // Update refund amount when booths are selected or fees change
  React.useEffect(() => {
    const additionalAmount = parseFloat(additionalRefundAmount) || 0;
    const additionalAmountInCents = Math.round(additionalAmount * 100);
    
    // Calculate if there are booths OR attendees to refund
    if (selectedBooths.length > 0 || (refundAttendeeCount > 0 && isAttendeePurchase)) {
      const finalAmount = calculateFinalRefundAmount();
      const totalAmount = finalAmount + additionalAmountInCents;
      setRefundAmount((totalAmount / 100).toFixed(2));
    } else if (additionalAmountInCents > 0) {
      // If no booths/attendees selected but additional amount is present
      setRefundAmount((additionalAmountInCents / 100).toFixed(2));
    } else {
      setRefundAmount('');
    }
  }, [selectedBooths, additionalRefundAmount, refundAttendeeCount, isAttendeePurchase, calculateFinalRefundAmount]);

  // Handle refund submission
  const handleRefundSubmit = async (e) => {
    e.preventDefault();
    
    setRefundError('');
    setRefundSuccess('');
    
    // Parse additional refund amount
    const additionalAmount = parseFloat(additionalRefundAmount) || 0;
    const additionalAmountInCents = Math.round(additionalAmount * 100);
    
    // Parse attendee count
    const attendeeCount = parseInt(refundAttendeeCount) || 0;
    
    // Validation - require either booths selected OR additional refund amount OR attendee count
    if (selectedBooths.length === 0 && additionalAmountInCents === 0 && attendeeCount === 0) {
      setRefundError('Please select at least one booth to refund, enter an attendee count, or enter an additional refund amount');
      return;
    }

    const amountInCents = Math.round(parseFloat(refundAmount) * 100);
    if (isNaN(amountInCents) || amountInCents <= 0) {
      setRefundError('Please enter a valid refund amount');
      return;
    }
    
    // Check that total refunds (including this one) don't exceed the original transaction amount
    const totalRefunded = transaction.totalRefunded || 0;
    const totalAfterThisRefund = totalRefunded + amountInCents;
    if (totalAfterThisRefund > transaction.amount) {
      setRefundError(`Total refunds cannot exceed the original transaction amount of ${formatCurrency(transaction.amount)}`);
      return;
    }
    
    if (!refundReason.trim()) {
      setRefundError('Please provide a reason for the refund');
      return;
    }

    const boothNumbers = selectedBooths.map(boothId => {
      const booth = transaction.booths.find(b => b.id === boothId);
      return booth?.number;
    }).filter(Boolean).join(', ');
    
    const confirmMessage = selectedBooths.length > 0
      ? `Are you sure you want to refund ${formatCurrency(amountInCents)} for this transaction?\n\n` +
        `Booths to refund: ${boothNumbers}\n` +
        (additionalAmountInCents > 0 ? `Additional refund: ${formatCurrency(additionalAmountInCents)}\n` : '') +
        `Reason: ${refundReason}\n\n` +
        `Selected booths will be released and made available for others.\n\n` +
        `This action cannot be undone.`
      : `Are you sure you want to refund ${formatCurrency(amountInCents)} for this transaction?\n\n` +
        `Additional refund amount: ${formatCurrency(additionalAmountInCents)}\n` +
        `Reason: ${refundReason}\n\n` +
        `This action cannot be undone.`;
    
    const confirmed = window.confirm(confirmMessage);
    
    if (!confirmed) return;
    
    try {
      setRefundProcessing(true);

      // Calculate refund breakdown
      const boothsTotal = selectedBooths.length > 0 ? calculateSelectedBoothsTotal() : 0;
      let refundFee = 0;

      if (selectedBooths.length > 0) {
        // Booth refund - calculate fee based on booths
        refundFee = calculateRefundFee(boothsTotal);
      } else if (isAttendeePurchase && (attendeeCount > 0 || additionalAmountInCents > 0)) {
        // Attendee refund - calculate fee based on attendee refund amount
        const attendeeRefundBase = attendeeCount > 0
          ? attendeeCount * (transaction.metadata?.pricePerAttendee ? Math.round(transaction.metadata.pricePerAttendee * 100) : 0)
          : additionalAmountInCents;
        refundFee = calculateRefundFee(attendeeRefundBase);
      }

      // Process the refund with Stripe
      await processRefund(transaction.paymentIntentId, {
        amount: amountInCents,
        reason: refundReason,
        boothIds: selectedBooths, // Pass selected booth IDs
        boothsTotal: boothsTotal, // Total amount for refunded booths
        refundFee: refundFee, // Refund fee deducted
        additionalRefund: additionalAmountInCents, // Additional refund amount
        eventId: transaction.eventId,
        attendeeCount: isAttendeePurchase ? attendeeCount : undefined,
      });
      
      // Release the selected booths
      const vendorId = transaction.metadata?.vendorId || vendorData?.id || '';
      // Use the current user's active session for this event/layout
      const currentSessionKey = `cartSession:${currentUser?.uid}:${transaction.eventId}:${transaction.layoutId}`;
      const sessionId = sessionStorage.getItem(currentSessionKey) || transaction.metadata?.sessionId || null;
      if (!vendorId) {
    // Removed console.warn
      }
      const boothReleasePromises = selectedBooths.map(async (boothId) => {
        try {
          let vendorIdForRelease = vendorId;
          const sessionIdForRelease = sessionId;
          // Fallback: resolve vendorId from reservation doc if still missing.
          if (!vendorIdForRelease && transaction.eventId && transaction.layoutId && boothId) {
            try {
              const reservationId = `${transaction.eventId}_${transaction.layoutId}_${boothId}`;
              const reservationSnap = await getDoc(doc(db, 'boothReservations', reservationId));
              if (reservationSnap.exists()) {
                const reservationData = reservationSnap.data() || {};
                if (!vendorIdForRelease) vendorIdForRelease = reservationData.vendorId || '';
              }
            } catch (lookupErr) {
              // Best effort fallback only.
            }
          }

          if (!vendorIdForRelease) {
            throw new Error('Missing vendorId for refund release');
          }

          await releaseBoothRemote({
            eventId: transaction.eventId,
            layoutId: transaction.layoutId,
            boothId: boothId,
            vendorId: vendorIdForRelease,
            createdByProfileId: currentUser?.uid,
            sessionId: sessionIdForRelease,
            triggeredBy: 'refund-release',
            refund: true,
          });
          return { boothId, released: true };
        } catch (err) {
          console.error(`Failed to release booth ${boothId}:`, err);
          return { boothId, released: false, error: err?.message || String(err) };
        }
      });
      
      const boothReleaseResults = await Promise.all(boothReleasePromises);
      const releasedCount = boothReleaseResults.filter(r => r?.released).length;
      const failedReleases = boothReleaseResults.filter(r => r && !r.released);
      
      // If this is an attendee purchase refund, decrease the purchased capacity
      if (isAttendeePurchase && attendeeCount > 0) {
        try {
          const vendorId = transaction.metadata?.vendorId || '';
          if (vendorId) {
            
            await decreasePurchasedCapacity(
              vendorId,
              transaction.eventId,
              attendeeCount
            );
          } // else block intentionally left empty
        } catch (err) {
          // Removed console.error

          // Don't block the refund process if capacity update fails
        }
      }
      
      // Mark cart items for refunded booths as 'removed'
      try {
  
        
        // Load all 'paid' cart items for this event/layout/profile
        const paidCartItems = await loadCartItemsByStatus({
          profileId: transaction.profileId,
          eventId: transaction.eventId,
          layoutId: transaction.layoutId,
          status: 'paid'
        });
        
        
        // Filter to only the booths being refunded
        const refundedCartItems = paidCartItems.filter(item => 
          selectedBooths.includes(item.boothId)
        );
        
        
        // Mark each as 'removed'
        for (const cartItem of refundedCartItems) {
          try {
            await updateCartItemAction(cartItem.cartItemId || cartItem.id, 'removed', {
              triggeredByProfileIdOverride: currentUser.uid,
              triggeredByOverride: 'booth-refunded'
            });
          } catch (err) {
            console.error(`Failed to update cart item ${cartItem.id} as removed:`, err);
          }
        }
      } catch (err) {
    // Removed console.error
        // Don't block the refund process if cart update fails
      }
      
      setRefundSuccess(`Successfully refunded ${formatCurrency(amountInCents)} and released ${releasedCount}/${selectedBooths.length} booth(s)`);
      if (failedReleases.length > 0) {
        const failedBooths = failedReleases.map(r => r.boothId).join(', ');
        setRefundError(`Refund succeeded, but failed to release booth(s): ${failedBooths}. Please retry release or contact support.`);
      }
      setShowRefundForm(false);
      setSelectedBooths([]);
      setSelectAllBooths(false);
      
      // Reload transaction to show updated status
      const updatedTxn = await getTransaction(transactionId);
      setTransaction(updatedTxn);

      // Notify Salesforce about vendor update (non-blocking)
      try {
        const vendorIdForSf = transaction.metadata?.vendorId || '';
        const eventIdForSf = transaction.eventId || '';
        if (vendorIdForSf && eventIdForSf) {
          getFunctionConfig()
            .then(appSettings => {
              sfVendorRegistration(appSettings, eventIdForSf, vendorIdForSf).catch(err => {
                if (appSettings.enableLogs) console.warn('[TransactionDetail] sfVendorRegistration failed (non-blocking):', err && err.message ? err.message : err);
              });
            })
            .catch(() => {});
        }
      } catch (e) {
        // non-blocking
      }
      
    } catch (err) {
  // Removed console.error
      setRefundError(err?.message || 'Failed to process refund');
    } finally {
      setRefundProcessing(false);
    }
  };

  // Check if refund is allowed
  const canRefund = () => {
    if (!transaction || !isAdmin) return false;
    const status = (transaction.status || '').toLowerCase();
    // Can refund succeeded or partially_refunded transactions
    if (status !== 'succeeded' && status !== 'partially_refunded') return false;
    // Must have refundable amount remaining
    const refundable = getRefundableAmount();
    if (refundable <= 0) return false;
    return true;
  };

  // Get refund disabled reason (for display)
  const getRefundDisabledReason = () => {
    if (!isAdmin) return 'Admin access required';
    if (!transaction) return 'Transaction not found';
    const status = (transaction.status || '').toLowerCase();
    if (status === 'refunded') return 'This transaction has already been fully refunded';
    if (status === 'failed' || status === 'incomplete') return 'Cannot refund unsuccessful transactions';
    const refundable = getRefundableAmount();
    if (refundable <= 0) return 'No refundable amount remaining';
    return '';
  };

  if (loading) {
    return (
      <div className="profile-container">
        <div className="profile-header-row">
          <button type="button" className="back-btn" onClick={() => navigate(-1)}>← Back</button>
        </div>
        <div style={{ padding: '40px', textAlign: 'center' }}>Loading transaction details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="profile-container">
        <div className="profile-header-row">
          <button type="button" className="back-btn" onClick={() => navigate(-1)}>← Back</button>
        </div>
        <div className="profile-error" style={{ marginTop: 20 }}>{error}</div>
      </div>
    );
  }

  if (!transaction) {
    return (
      <div className="profile-container">
        <div className="profile-header-row">
          <button type="button" className="back-btn" onClick={() => navigate(-1)}>← Back</button>
        </div>
        <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>Transaction not found</div>
      </div>
    );
  }

  return (
    <div className="profile-container" style={{ maxWidth: '1400px' }}>
      <div className="profile-header-row">
        <button type="button" className="back-btn" onClick={() => navigate(-1)}>← Back</button>
        <div style={{ flex: 1 }} />
        <button type="button" className="cancel-btn" onClick={() => navigate('/transactions')}>Close</button>
      </div>

      <h2 className="profile-title">Transaction Details</h2>

      {refundSuccess && (
        <div style={{
          padding: '12px',
          backgroundColor: '#d1fae5',
          color: '#065f46',
          borderRadius: '4px',
          marginBottom: '16px',
          fontWeight: 500
        }}>
          {refundSuccess}
        </div>
      )}

      {/* Transaction Status */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{
            display: 'inline-block',
            padding: '8px 16px',
            borderRadius: '6px',
            fontSize: '16px',
            fontWeight: 700,
            color: '#fff',
            backgroundColor: getStatusColor(transaction.status),
          }}>
            {(transaction.status || 'unknown').toUpperCase()}
          </span>
          {transaction.attemptCount > 0 && (
            <span style={{
              fontSize: '14px',
              color: transaction.status === 'succeeded' ? '#059669' : '#6b7280',
              fontWeight: 500
            }}>
              {transaction.status === 'succeeded'
                ? `✓ Succeeded after ${transaction.attemptCount} ${transaction.attemptCount === 1 ? 'attempt' : 'attempts'}`
                : `${transaction.attemptCount} ${transaction.attemptCount === 1 ? 'attempt' : 'attempts'}`
              }
            </span>
          )}
        </div>
      </div>

      {/* Transaction Information */}
      <div style={{ marginBottom: 24 }}>
        <h3 style={{ marginBottom: 12, fontSize: '18px', fontWeight: 600 }}>Transaction Information</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: '12px', fontSize: '14px' }}>
          <div style={{ fontWeight: 600, color: '#6b7280' }}>Payment Intent ID:</div>
          <div style={{ fontFamily: 'monospace', fontSize: '13px' }}>{transaction.paymentIntentId || transaction.id}</div>

          <div style={{ fontWeight: 600, color: '#6b7280' }}>Created:</div>
          <div>{formatTimestamp(transaction.createdAt)}</div>

          <div style={{ fontWeight: 600, color: '#6b7280' }}>Last Updated:</div>
          <div>{formatTimestamp(transaction.updatedAt)}</div>

          <div style={{ fontWeight: 600, color: '#6b7280' }}>Original Amount:</div>
          <div style={{ fontSize: '18px', fontWeight: 700, color: '#111' }}>{formatCurrency(transaction.amount)}</div>

          {transaction.totalRefunded > 0 && (
            <>
              {/* Calculate and display cancellation fees and additional refund separately */}
              {(() => {
                let totalCancellationFees = 0;
                let totalAdditionalRefunds = 0;
                if (transaction.refundHistory && transaction.refundHistory.length > 0) {
                  transaction.refundHistory.forEach(refund => {
                    if (refund.status === 'succeeded') {
                      // Get the booths that were refunded
                      const refundedBoothIds = refund.boothIds || [];
                      let boothTotal = 0;
                      refundedBoothIds.forEach(boothId => {
                        const booth = transaction.booths?.find(b => b.id === boothId);
                        if (booth) {
                          boothTotal += booth.price || 0;
                        }
                      });
                      // Cancellation fee is the difference between booth total and refund amount minus any additional refund
                      const additionalRefund = refund.additionalRefund || 0;
                      const fee = Math.max(0, boothTotal - ((refund.amount || 0) - additionalRefund));
                      totalCancellationFees += fee;
                      totalAdditionalRefunds += additionalRefund;
                    }
                  });
                }
                return (
                  <>
                    {totalCancellationFees > 0 && (
                      <>
                        <div style={{ fontWeight: 600, color: '#6b7280' }}>Cancellation Fees:</div>
                        <div style={{ fontSize: '16px', fontWeight: 600, color: '#ef4444' }}>{formatCurrency(totalCancellationFees)}</div>
                      </>
                    )}
                    {totalAdditionalRefunds > 0 && (
                      <>
                        <div style={{ fontWeight: 600, color: '#6b7280' }}>Additional Refund:</div>
                        <div style={{ fontSize: '16px', fontWeight: 600, color: '#10b981' }}>{formatCurrency(totalAdditionalRefunds)}</div>
                      </>
                    )}
                  </>
                );
              })()}

              <div style={{ fontWeight: 600, color: '#6b7280' }}>Total Refunded:</div>
              <div style={{ fontSize: '16px', fontWeight: 600, color: '#10b981' }}>{formatCurrency(transaction.totalRefunded)}</div>

              <div style={{ fontWeight: 600, color: '#6b7280' }}>Remaining Balance:</div>
              <div style={{ fontSize: '16px', fontWeight: 600, color: '#9333ea' }}>{formatCurrency(getRefundableAmount())}</div>
            </>
          )}

          <div style={{ fontWeight: 600, color: '#6b7280' }}>Currency:</div>
          <div>{(transaction.currency || 'USD').toUpperCase()}</div>

          <div style={{ fontWeight: 600, color: '#6b7280' }}>Event:</div>
          <div>{transaction.metadata?.eventName || transaction.eventId || 'N/A'}</div>
        </div>
      </div>

      {/* Vendor Information */}
      <div style={{ marginBottom: 24 }}>
        <h3 style={{ marginBottom: 12, fontSize: '18px', fontWeight: 600 }}>Vendor Information</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: '12px', fontSize: '14px' }}>
          <div style={{ fontWeight: 600, color: '#6b7280' }}>Vendor Name:</div>
          <div>{transaction.metadata?.vendorName || vendorData?.name || vendorProfile?.businessName || 'N/A'}</div>

          <div style={{ fontWeight: 600, color: '#6b7280' }}>Vendor Number:</div>
          <div>{transaction.metadata?.vendorNumber || vendorData?.vendorNumber || vendorProfile?.vendorNumber || 'N/A'}</div>

          <div style={{ fontWeight: 600, color: '#6b7280' }}>Contact Name:</div>
          <div>
            {vendorProfile ? `${vendorProfile.firstName || ''} ${vendorProfile.lastName || ''}`.trim() : transaction.metadata?.payeeName || 'N/A'}
          </div>

          <div style={{ fontWeight: 600, color: '#6b7280' }}>Contact Email:</div>
          <div>{vendorProfile?.email || transaction.metadata?.payeeEmail || 'N/A'}</div>

          <div style={{ fontWeight: 600, color: '#6b7280' }}>Contact Phone:</div>
          <div>{vendorProfile?.phone || transaction.metadata?.payeePhone || 'N/A'}</div>
        </div>
      </div>

      {/* Metadata Modal */}
      {showMetadataModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowMetadataModal(false)}>
          <div style={{ width: '90%', maxWidth: 800, maxHeight: '80%', overflow: 'auto', backgroundColor: '#fff', borderRadius: 8, padding: 20, boxShadow: '0 8px 24px rgba(0,0,0,0.2)' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>Transaction Metadata</h3>
              <button className="cancel-btn" onClick={() => setShowMetadataModal(false)}>Close</button>
            </div>

            <div style={{ fontSize: 14, color: '#333' }}>
              {transaction.metadata && Object.keys(transaction.metadata).length > 0 ? (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <tbody>
                    {Object.entries(transaction.metadata)
                      .sort((a, b) => a[0].toString().localeCompare(b[0].toString()))
                      .map(([k, v]) => (
                        <tr key={k} style={{ borderBottom: '1px solid #eee' }}>
                          <td style={{ padding: '8px 12px', verticalAlign: 'top', width: '35%', fontWeight: 600, color: '#555' }}>{k}</td>
                          <td style={{ padding: '8px 12px', verticalAlign: 'top', color: '#111' }}>
                            {typeof v === 'object' && v !== null ? (
                              <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{JSON.stringify(v, null, 2)}</pre>
                            ) : (
                              String(v)
                            )}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              ) : (
                <div style={{ padding: 12, color: '#6b7280' }}>No metadata available for this transaction.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Booth Information */}
      {transaction.booths && transaction.booths.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ marginBottom: 12, fontSize: '18px', fontWeight: 600 }}>Booth Details</h3>
          {isAdmin && canRefund() && (
            <div style={{ 
              marginBottom: 12, 
              padding: '8px 12px', 
              backgroundColor: '#eff6ff', 
              borderRadius: '4px',
              fontSize: '14px'
            }}>
              Select booths to refund. The refund amount will be calculated based on your selection.
            </div>
          )}
          <table className="profile-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                {isAdmin && canRefund() && (
                  <th style={{ textAlign: 'center', padding: '8px', width: '50px' }}>
                    <input
                      type="checkbox"
                      checked={selectAllBooths}
                      onChange={(e) => handleSelectAllBooths(e.target.checked)}
                      style={{ cursor: 'pointer' }}
                    />
                  </th>
                )}
                <th style={{ textAlign: 'left', padding: '8px' }}>Booth #</th>
                <th style={{ textAlign: 'left', padding: '8px' }}>Level</th>
                <th style={{ textAlign: 'left', padding: '8px' }}>Location</th>
                <th style={{ textAlign: 'left', padding: '8px' }}>Type</th>
                <th style={{ textAlign: 'right', padding: '8px' }}>Price</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                const startIndex = (currentPage - 1) * itemsPerPage;
                const endIndex = startIndex + itemsPerPage;
                const paginatedBooths = transaction.booths.slice(startIndex, endIndex);
                
                return paginatedBooths.map((booth, idx) => {
                  const refunded = isBoothRefunded(booth.id);
                  return (
                    <tr key={idx} style={{ 
                      opacity: refunded ? 0.5 : 1,
                      backgroundColor: refunded ? '#f9fafb' : 'transparent'
                    }}>
                      {isAdmin && canRefund() && (
                        <td style={{ padding: '8px', borderTop: '1px solid #eee', textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={selectedBooths.includes(booth.id)}
                            onChange={(e) => handleBoothSelection(booth.id, e.target.checked)}
                            disabled={refunded}
                            style={{ cursor: refunded ? 'not-allowed' : 'pointer' }}
                          />
                        </td>
                      )}
                      <td style={{ padding: '8px', borderTop: '1px solid #eee', fontWeight: 600 }}>
                        {booth.number}
                        {refunded && (
                          <span style={{
                            marginLeft: 8,
                            padding: '2px 6px',
                            fontSize: '11px',
                            fontWeight: 600,
                            backgroundColor: '#9333ea',
                            color: 'white',
                            borderRadius: '4px'
                          }}>
                            REFUNDED
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>{booth.level || 'N/A'}</td>
                      <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>Row {booth.row}, Col {booth.col}</td>
                      <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>{booth.corner === 'Y' ? 'Corner' : 'Standard'}</td>
                      <td style={{ padding: '8px', borderTop: '1px solid #eee', textAlign: 'right', fontWeight: 600 }}>
                        {formatCurrency(booth.price)}
                      </td>
                    </tr>
                  );
                });
              })()}
            </tbody>
          </table>
          
          {/* Pagination Controls */}
          {transaction.booths.length > itemsPerPage && (
            <div style={{ 
              marginTop: 12, 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              gap: 12 
            }}>
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                style={{
                  padding: '6px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  backgroundColor: currentPage === 1 ? '#f3f4f6' : 'white',
                  color: currentPage === 1 ? '#9ca3af' : '#374151',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  fontWeight: 500
                }}
              >
                Previous
              </button>
              <span style={{ fontSize: '14px', color: '#374151', fontWeight: 500 }}>
                Page {currentPage} of {Math.ceil(transaction.booths.length / itemsPerPage)} 
                ({transaction.booths.length} total booths)
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(Math.ceil(transaction.booths.length / itemsPerPage), prev + 1))}
                disabled={currentPage === Math.ceil(transaction.booths.length / itemsPerPage)}
                style={{
                  padding: '6px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  backgroundColor: currentPage === Math.ceil(transaction.booths.length / itemsPerPage) ? '#f3f4f6' : 'white',
                  color: currentPage === Math.ceil(transaction.booths.length / itemsPerPage) ? '#9ca3af' : '#374151',
                  cursor: currentPage === Math.ceil(transaction.booths.length / itemsPerPage) ? 'not-allowed' : 'pointer',
                  fontWeight: 500
                }}
              >
                Next
              </button>
            </div>
          )}
          
          {isAdmin && canRefund() && selectedBooths.length > 0 && (
            <div style={{ 
              marginTop: 12, 
              padding: '12px', 
              backgroundColor: '#f0fdf4', 
              borderRadius: '4px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              fontWeight: 600
            }}>
              <span>{selectedBooths.length} booth(s) selected</span>
              <span style={{ color: '#059669' }}>Total: {formatCurrency(calculateSelectedBoothsTotal())}</span>
            </div>
          )}
        </div>
      )}

      {/* Failure History */}
      {transaction.failureAttempts && transaction.failureAttempts.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ marginBottom: 12, fontSize: '18px', fontWeight: 600, color: '#ef4444' }}>Failure History</h3>
          <div style={{ backgroundColor: '#fef2f2', padding: '12px', borderRadius: '4px' }}>
            {transaction.failureAttempts.map((attempt, idx) => (
              <div key={idx} style={{ marginBottom: idx < transaction.failureAttempts.length - 1 ? 12 : 0 }}>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: 4 }}>
                  Attempt #{idx + 1} - {formatTimestamp(attempt.attemptedAt)}
                </div>
                <div style={{ fontSize: '14px', color: '#dc2626' }}>{attempt.reason}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Refund History */}
      {transaction.refundHistory && transaction.refundHistory.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ marginBottom: 12, fontSize: '18px', fontWeight: 600, color: '#9333ea' }}>Refund History</h3>
          <div style={{ overflowX: 'auto' }}>
            <table className="profile-table" style={{ width: '100%', minWidth: '1000px' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '8px' }}>Refund ID</th>
                <th style={{ textAlign: 'left', padding: '8px' }}>Date</th>
                <th style={{ textAlign: 'left', padding: '8px' }}>Booths</th>
                <th style={{ textAlign: 'right', padding: '8px' }}>Booth Total</th>
                <th style={{ textAlign: 'right', padding: '8px' }}>Cancellation Fee</th>
                <th style={{ textAlign: 'right', padding: '8px' }}>Additional Refund</th>
                <th style={{ textAlign: 'right', padding: '8px' }}>Refund Amount</th>
                <th style={{ textAlign: 'left', padding: '8px' }}>Reason</th>
                <th style={{ textAlign: 'left', padding: '8px' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {transaction.refundHistory.map((refund, idx) => {
                // Get booth numbers from refund history
                const refundedBoothIds = refund.boothIds || [];
                const refundedBoothNumbers = refundedBoothIds.map(boothId => {
                  const booth = transaction.booths?.find?.(b => b.id === boothId);
                  return booth?.number;
                }).filter(Boolean).join(', ') || 'N/A';

                // Use stored breakdown values (available for new refunds)
                const boothTotal = refund.boothsTotal || 0;
                const cancellationFee = refund.refundFee || 0;
                const additionalRefund = refund.additionalRefund || 0;

                // For legacy refunds without breakdown, calculate from booths
                let calculatedBoothTotal = boothTotal;
                if (!boothTotal && refundedBoothIds.length > 0) {
                  refundedBoothIds.forEach(boothId => {
                    const booth = transaction.booths?.find?.(b => b.id === boothId);
                    if (booth) {
                      calculatedBoothTotal += booth.price || 0;
                    }
                  });
                }

                // For attendee refunds, show attendee info and cancellation fee
                const isAttendeeRefund = transaction?.metadata?.purchaseType === 'attendees' || refund.attendeeCount;
                const attendeeCount = refund.attendeeCount || transaction?.metadata?.attendeeCount || 0;
                const attendeePrice = transaction?.metadata?.pricePerAttendee || 0;

                return (
                  <tr key={idx}>
                    <td style={{ padding: '8px', borderTop: '1px solid #eee', fontFamily: 'monospace', fontSize: '12px' }}>
                      {refund.refundId}
                    </td>
                    <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>{formatTimestamp(refund.refundedAt)}</td>
                    <td style={{ padding: '8px', borderTop: '1px solid #eee', fontWeight: 600 }}>
                      {isAttendeeRefund
                        ? `${refund.attendeeCount || attendeeCount} attendee(s)`
                        : refundedBoothNumbers}
                    </td>
                    <td style={{ padding: '8px', borderTop: '1px solid #eee', textAlign: 'right', fontWeight: 600, color: '#111' }}>
                      {isAttendeeRefund
                        ? formatCurrency((refund.attendeeCount || attendeeCount) * (attendeePrice * 100))
                        : formatCurrency(calculatedBoothTotal)}
                    </td>
                    <td style={{ padding: '8px', borderTop: '1px solid #eee', textAlign: 'right', fontWeight: 600, color: '#ef4444' }}>
                      {cancellationFee > 0 ? formatCurrency(cancellationFee) : '-'}
                    </td>
                    <td style={{ padding: '8px', borderTop: '1px solid #eee', textAlign: 'right', fontWeight: 600, color: '#10b981' }}>
                      {additionalRefund > 0 ? formatCurrency(additionalRefund) : '-'}
                    </td>
                    <td style={{ padding: '8px', borderTop: '1px solid #eee', textAlign: 'right', fontWeight: 600, color: '#10b981' }}>
                      {formatCurrency(refund.amount)}
                    </td>
                    <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>{refund.reason || 'N/A'}</td>
                    <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>
                      <span style={{
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: 600,
                        color: '#fff',
                        backgroundColor: refund.status === 'succeeded' ? '#10b981' : '#6b7280'
                      }}>
                        {refund.status.toUpperCase()}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* Refund Actions - Admin Only */}
      {isAdmin && (
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ marginBottom: 12, fontSize: '18px', fontWeight: 600 }}>Refund Actions</h3>
          
          {!canRefund() && (
            <div style={{
              padding: '12px',
              backgroundColor: '#fef3c7',
              color: '#92400e',
              borderRadius: '4px',
              marginBottom: '12px'
            }}>
              {getRefundDisabledReason()}
            </div>
          )}
          
          {canRefund() && !showRefundForm && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button
                type="button"
                onClick={() => setShowRefundForm(true)}
                style={{ 
                  backgroundColor: '#9333ea',
                  color: '#fff',
                  padding: '12px 32px',
                  fontSize: '16px',
                  fontWeight: 600,
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  minWidth: '180px'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#7e22ce'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#9333ea'}
              >
                Issue Refund
              </button>

              <button
                type="button"
                onClick={() => setShowMetadataModal(true)}
                style={{ 
                  backgroundColor: '#1976d2',
                  color: '#fff',
                  padding: '12px 20px',
                  fontSize: '14px',
                  fontWeight: 600,
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  minWidth: '140px'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#0f5fb0'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#1976d2'}
              >
                Show Metadata
              </button>
            </div>
          )}

          {showRefundForm && (
            <form onSubmit={handleRefundSubmit} style={{
              padding: '16px',
              backgroundColor: '#faf5ff',
              borderRadius: '8px',
              border: '2px solid #9333ea'
            }}>
              {selectedBooths.length === 0 && !additionalRefundAmount && (
                <div style={{
                  padding: '12px',
                  backgroundColor: '#fef3c7',
                  color: '#92400e',
                  borderRadius: '4px',
                  marginBottom: '12px',
                  fontSize: '14px'
                }}>
                  Please select at least one booth from the Booth Details table above OR enter an additional refund amount below.
                </div>
              )}

              {selectedBooths.length > 0 && (
                <div style={{
                  padding: '12px',
                  backgroundColor: '#f0fdf4',
                  borderRadius: '4px',
                  marginBottom: '12px'
                }}>
                  <div style={{ fontWeight: 600, marginBottom: 8, color: '#065f46' }}>
                    Selected Booths for Refund:
                  </div>
                  <div style={{ fontSize: '14px', color: '#059669' }}>
                    {selectedBooths.map(boothId => {
                      const booth = transaction.booths.find(b => b.id === boothId);
                      return booth?.number;
                    }).filter(Boolean).join(', ')}
                  </div>
                  <div style={{ fontSize: '14px', marginTop: 8, fontWeight: 600, color: '#059669' }}>
                    Total: {formatCurrency(calculateSelectedBoothsTotal())}
                  </div>
                </div>
              )}

              {/* Refund Fee Configuration - Always show */}
              <div style={{ 
                marginBottom: 12,
                padding: '16px',
                backgroundColor: '#fffbeb',
                border: '1px solid #fbbf24',
                borderRadius: '4px'
              }}>
                <h4 style={{ marginTop: 0, marginBottom: 12, fontSize: '16px', fontWeight: 600, color: '#92400e' }}>
                  Refund Fee Configuration
                </h4>
                <div style={{ fontSize: '12px', color: '#78350f', marginBottom: 12 }}>
                  Default values are from the event settings. You can adjust them if needed.
                </div>
                
                {/* Additional Refund Amount Field */}
                <div style={{ marginBottom: 16 }}>
                  <label className="profile-label" style={{ fontSize: '13px', fontWeight: 600, color: '#1e40af' }}>
                    Additional Refund Amount ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className="profile-input"
                    value={additionalRefundAmount}
                    onChange={(e) => setAdditionalRefundAmount(e.target.value)}
                    disabled={refundProcessing}
                    placeholder="0.00"
                    style={{ fontSize: '14px' }}
                  />
                  <div className="profile-hint" style={{ marginTop: 4, fontSize: '11px' }}>
                    Use this field to add a refund amount that is not tied to specific booths (e.g., processing fees, adjustments)
                  </div>
                </div>
                
                {/* Attendee Count Field - Only show for attendee purchases */}
                {transaction?.metadata?.purchaseType === 'attendees' && (
                  <div style={{ marginBottom: 16 }}>
                    <label className="profile-label" style={{ fontSize: '13px', fontWeight: 600, color: '#1e40af' }}>
                      Number of Attendees to Refund
                    </label>
                    <input
                      type="number"
                      min="0"
                      max={parseInt(transaction?.metadata?.attendeeCount) || 0}
                      className="profile-input"
                      value={refundAttendeeCount}
                      onChange={(e) => setRefundAttendeeCount(e.target.value)}
                      disabled={refundProcessing}
                      placeholder="0"
                      style={{ fontSize: '14px' }}
                    />
                    <div className="profile-hint" style={{ marginTop: 4, fontSize: '11px' }}>
                      Total attendees purchased: {transaction?.metadata?.attendeeCount || 0} at ${transaction?.metadata?.pricePerAttendee || 0} each
                    </div>
                  </div>
                )}
                
                {(selectedBooths.length > 0 || (transaction?.metadata?.purchaseType === 'attendees' && refundAttendeeCount > 0)) && (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                      <div>
                        <label className="profile-label" style={{ fontSize: '13px' }}>Refund Fee Percentage (%)</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          className="profile-input"
                          value={refundFeePercentage}
                          onChange={(e) => setRefundFeePercentage(e.target.value)}
                          disabled={refundProcessing}
                          placeholder="0.00"
                          style={{ fontSize: '14px' }}
                        />
                      </div>
                      <div>
                        <label className="profile-label" style={{ fontSize: '13px' }}>Refund Fee Amount ($)</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          className="profile-input"
                          value={refundFeeAmount}
                          onChange={(e) => setRefundFeeAmount(e.target.value)}
                          disabled={refundProcessing}
                          placeholder="0.00"
                          style={{ fontSize: '14px' }}
                        />
                      </div>
                    </div>
                    
                    <div style={{ marginBottom: 8 }}>
                      <label style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 8, 
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: 500
                      }}>
                        <input
                          type="checkbox"
                          checked={applyRefundFeePerBooth}
                          onChange={(e) => setApplyRefundFeePerBooth(e.target.checked)}
                          disabled={refundProcessing}
                          style={{ cursor: 'pointer' }}
                        />
                        <span>Apply Refund Fee Amount Per {transaction?.metadata?.purchaseType === 'attendees' ? 'Attendee' : 'Booth'}</span>
                      </label>
                      <div className="profile-hint" style={{ marginTop: 4, marginLeft: 24, fontSize: '11px' }}>
                        If checked, the fixed fee amount will be multiplied by the number of {transaction?.metadata?.purchaseType === 'attendees' ? 'attendees being refunded' : 'selected booths'}
                      </div>
                    </div>
                  </>
                )}

                {/* Fee Breakdown */}
                {(selectedBooths.length > 0 || parseFloat(additionalRefundAmount) > 0) && (
                  <div style={{ 
                    marginTop: 16, 
                    paddingTop: 12, 
                    borderTop: '1px solid #fbbf24',
                    fontSize: '14px'
                  }}>
                    {selectedBooths.length > 0 && (
                      <>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                          <span>Booth Total:</span>
                          <span style={{ fontWeight: 600 }}>{formatCurrency(calculateSelectedBoothsTotal())}</span>
                        </div>
                        {(refundFeePercentage || refundFeeAmount) && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, color: '#dc2626' }}>
                            <span>
                              Refund Fee:
                              {refundFeePercentage && ` (${refundFeePercentage}%)`}
                              {refundFeeAmount && refundFeePercentage && ' + '}
                              {refundFeeAmount && ` ($${refundFeeAmount}${applyRefundFeePerBooth ? ` × ${selectedBooths.length}` : ''})`}
                            </span>
                            <span style={{ fontWeight: 600 }}>-{formatCurrency(calculateRefundFee(calculateSelectedBoothsTotal()))}</span>
                          </div>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                          <span>Net Booth Refund:</span>
                          <span style={{ fontWeight: 600 }}>{formatCurrency(calculateFinalRefundAmount())}</span>
                        </div>
                      </>
                    )}
                    {parseFloat(additionalRefundAmount) > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, color: '#1e40af' }}>
                        <span>Additional Refund:</span>
                        <span style={{ fontWeight: 600 }}>+{formatCurrency(Math.round(parseFloat(additionalRefundAmount) * 100))}</span>
                      </div>
                    )}
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      paddingTop: 8, 
                      borderTop: '1px solid #fbbf24',
                      fontWeight: 700,
                      fontSize: '16px',
                      color: '#065f46'
                    }}>
                      <span>Total Refund Amount:</span>
                      <span>
                        {formatCurrency(
                          (selectedBooths.length > 0 ? calculateFinalRefundAmount() : 0) + 
                          Math.round((parseFloat(additionalRefundAmount) || 0) * 100)
                        )}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <div style={{ marginBottom: 12 }}>
                <label className="profile-label">Refund Amount</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={((transaction?.amount || 0) / 100).toFixed(2)}
                  className="profile-input"
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)}
                  required
                  disabled={refundProcessing}
                  readOnly
                  style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                />
                <div className="profile-hint" style={{ marginTop: 4, fontSize: '12px' }}>
                  Amount is automatically calculated based on selected booths and additional refund amount
                </div>
              </div>

              <div style={{ marginBottom: 12 }}>
                <label className="profile-label">Reason for Refund</label>
                <textarea
                  className="profile-input"
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value)}
                  rows={3}
                  required
                  disabled={refundProcessing}
                  placeholder="Explain why this refund is being issued..."
                />
                <div className="profile-hint" style={{ marginTop: 4, fontSize: '12px' }}>
                  Your custom reason will be stored. Stripe will receive this as "requested_by_customer".
                </div>
              </div>

              {refundError && (
                <div className="profile-error" style={{ marginBottom: 12 }}>{refundError}</div>
              )}

              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="submit"
                  className="save-btn"
                  disabled={refundProcessing || (selectedBooths.length === 0 && !additionalRefundAmount && !refundAttendeeCount)}
                  style={{ 
                    backgroundColor: (selectedBooths.length === 0 && !additionalRefundAmount && !refundAttendeeCount) ? '#9ca3af' : '#9333ea',
                    cursor: (selectedBooths.length === 0 && !additionalRefundAmount && !refundAttendeeCount) ? 'not-allowed' : 'pointer',
                    whiteSpace: 'nowrap',
                    minWidth: '240px'
                  }}
                >
                  {refundProcessing ? 'Processing...' : 'Process Refund & Release Booths'}
                </button>
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => {
                    setShowRefundForm(false);
                    setRefundError('');
                    setSelectedBooths([]);
                    setSelectAllBooths(false);
                  }}
                  disabled={refundProcessing}
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
};

export default TransactionDetail;
